import React, { useState, useEffect } from 'react';
import { attendanceAPI } from '../../services/api';
import { Calendar, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { format, parseISO, getDay, setDate, subMonths, addMonths } from 'date-fns';
import { getStatusBadge, getRowHighlightClass, DateColumnDisplay, formatMinutes } from '../../utils/attendanceUtils';

const AttendanceRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const getPayrollCycle = () => {
    const today = new Date();
    if (today.getDate() >= 21) {
      return {
        start_date: format(setDate(today, 21), 'yyyy-MM-dd'),
        end_date: format(setDate(addMonths(today, 1), 20), 'yyyy-MM-dd'),
      };
    } else {
      return {
        start_date: format(setDate(subMonths(today, 1), 21), 'yyyy-MM-dd'),
        end_date: format(setDate(today, 20), 'yyyy-MM-dd'),
      };
    }
  };

  const [filters, setFilters] = useState({
    ...getPayrollCycle(),
    limit: 50
  });

  useEffect(() => {
    fetchRecords();
  }, [filters]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await attendanceAPI.getMyRecords(filters);
      setRecords(response.data.records);
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isNonWorkingDay = (record) => {
    const dayOfWeek = getDay(parseISO(record.workday));
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    return isWeekend || record.holiday_name || record.leave_code;
  };
  
  // Removed local getStatusBadge to share logic from attendanceUtils

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <Calendar className="h-6 w-6 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Attendance Records</h1>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            {/* <div>
              <label htmlFor="limit" className="block text-sm font-medium text-gray-700">
                Records Limit
              </label>
              <select
                id="limit"
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value={30}>30 records</option>
                <option value={50}>50 records</option>
                <option value={100}>100 records</option>
              </select>
            </div> */}
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Records ({records.length})
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No records found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your date range or filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clock In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clock Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Work Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => {
                    return (
                    <tr key={record.id} className={getRowHighlightClass(record.workday, false)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DateColumnDisplay workday={record.workday} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.clock_in ? (
                          <div className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            {format(parseISO(record.clock_in), 'HH:mm')}
                          </div>
                        ) : isNonWorkingDay(record) ? (
                          <div className="flex items-center">
                            <span className="text-gray-400">--:--</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <XCircle className="h-4 w-4 text-red-500 mr-2" />
                            --:--
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.clock_out ? (
                          <div className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            {format(parseISO(record.clock_out), 'HH:mm')}
                          </div>
                        ) : record.clock_in ? (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 text-blue-500 mr-2" />
                            In Progress
                          </div>
                        ) : isNonWorkingDay(record) ? (
                          <div className="flex items-center">
                            <span className="text-gray-400">--:--</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <XCircle className="h-4 w-4 text-red-500 mr-2" />
                            --:--
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMinutes(record.total_work_minutes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.remark || '--'}
                      </td>
                    </tr>
                  )
                })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {/* {records.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {records.filter(r => r.clock_in).length}
                </div>
                <div className="text-sm text-gray-500">Days Worked</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {records.filter(r => r.is_late).length}
                </div>
                <div className="text-sm text-gray-500">Late Days</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {records.filter(r => r.is_early_out).length}
                </div>
                <div className="text-sm text-gray-500">Early Outs</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {records.reduce((sum, r) => sum + (r.work_hours || 0), 0).toFixed(1)}h
                </div>
                <div className="text-sm text-gray-500">Total Hours</div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default AttendanceRecords;
